import { Link } from "react-router-dom";

import { getPublicProfileHref } from "../../../utils/publicProfile";
import { partyDisplayName } from "../lib/dealFormatting";
import {
  DEAL_SIDE_KIND,
  getCompanyWorkLinks,
} from "../lib/dealSidePresentation";

function PartyIdentity({ user }) {
  return (
    <>
      {user && user.user_image ? (
        <img
          src={user.user_image}
          alt=""
          aria-hidden="true"
          className="feature-deal-detail__party-avatar"
        />
      ) : (
        <span
          className="feature-deal-detail__party-avatar feature-deal-detail__party-avatar--fallback"
          aria-hidden="true"
        >
          <i className="fa-solid fa-user" />
        </span>
      )}
      <div>
        <div>{partyDisplayName(user)}</div>
        {user && user.handle ? <div>@{user.handle}</div> : null}
      </div>
    </>
  );
}

function CompanyIdentity({ presentation }) {
  return (
    <>
      <span
        className="feature-deal-detail__party-avatar feature-deal-detail__party-avatar--company"
        aria-hidden="true"
      >
        <i className="fa-solid fa-building" />
      </span>
      <div className="feature-deal-detail__party-company">
        <div className="feature-deal-detail__party-kind">
          {presentation.displayKindLabel}
        </div>
        <div className="feature-deal-detail__party-company-name">
          {presentation.displayName}
        </div>
      </div>
    </>
  );
}

/**
 * G2 party card — personal or company side from presentation adapter.
 * @param {{ presentation: object, children?: import("react").ReactNode }} props
 */
export function DealSidePartyCard({ presentation, children }) {
  if (!presentation) return null;

  const title = presentation.sideTitle;
  const isCompany = presentation.kind === DEAL_SIDE_KIND.COMPANY;
  const workLinks = getCompanyWorkLinks(presentation);

  if (isCompany) {
    const rep = presentation.representative;
    const repName = rep ? partyDisplayName(rep) : null;
    const repHref = getPublicProfileHref(rep);

    return (
      <article
        className="feature-deal-detail__party-card feature-deal-detail__party-card--company"
        data-side={presentation.side}
        data-side-kind="company"
        data-testid={`deal-side-card-${presentation.side}`}
      >
        <h3 className="feature-deal-detail__party-title">{title}</h3>
        <div className="feature-deal-detail__party-body">
          <CompanyIdentity presentation={presentation} />
        </div>
        <div className="feature-deal-detail__party-representative">
          <span className="feature-deal-detail__party-representative-label">
            Представник:
          </span>{" "}
          {rep && repName && repName !== "—" ? (
            repHref ? (
              <Link
                to={repHref}
                className="feature-deal-detail__party-link"
                aria-label={`Представник ${repName}`}
              >
                {repName}
                {rep.handle ? ` (@${rep.handle})` : ""}
              </Link>
            ) : (
              <span>
                {repName}
                {rep.handle ? ` (@${rep.handle})` : ""}
              </span>
            )
          ) : (
            <span className="feature-deal-detail__party-representative-absent">
              не вказано
            </span>
          )}
        </div>
        {workLinks ? (
          <div className="feature-deal-detail__party-work-links">
            <Link to={workLinks.tasksHref} className="action-btn">
              Завдання компанії
            </Link>
            <Link to={workLinks.viewingsHref} className="action-btn">
              Покази компанії
            </Link>
          </div>
        ) : null}
        {children}
      </article>
    );
  }

  const user = presentation.historicalRealtor;
  const href = getPublicProfileHref(user);
  const identityLabel = [
    partyDisplayName(user),
    user && user.handle ? `@${user.handle}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className="feature-deal-detail__party-card"
      data-side={presentation.side}
      data-side-kind="personal"
      data-testid={`deal-side-card-${presentation.side}`}
    >
      <h3 className="feature-deal-detail__party-title">{title}</h3>
      <p className="feature-deal-detail__party-kind feature-deal-detail__party-kind--personal">
        {presentation.displayKindLabel}
      </p>
      {href ? (
        <Link
          to={href}
          className="feature-deal-detail__party-body feature-deal-detail__party-link"
          aria-label={identityLabel || "Профіль партнера"}
        >
          <PartyIdentity user={user} />
        </Link>
      ) : (
        <div className="feature-deal-detail__party-body">
          <PartyIdentity user={user} />
        </div>
      )}
      {children}
    </article>
  );
}

/** Legacy personal-only card kept for existing unit tests. */
export function PartyCard({ title, user }) {
  const href = getPublicProfileHref(user);
  const identityLabel = [
    partyDisplayName(user),
    user && user.handle ? `@${user.handle}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className="feature-deal-detail__party-card">
      <h3 className="feature-deal-detail__party-title">{title}</h3>
      {href ? (
        <Link
          to={href}
          className="feature-deal-detail__party-body feature-deal-detail__party-link"
          aria-label={identityLabel || "Профіль партнера"}
        >
          <PartyIdentity user={user} />
        </Link>
      ) : (
        <div className="feature-deal-detail__party-body">
          <PartyIdentity user={user} />
        </div>
      )}
    </article>
  );
}

export default DealSidePartyCard;
